import {ImportadorIfoodService} from '../../services/importador-ifood.service';

export interface PlataformaDoLink { id: string; nome: string; }

/** Ordem importa: "cardapio.ai" está dentro de "meucardapio.ai", e "ifood" pode aparecer no caminho de outros links. */
const PLATAFORMAS: {id: string; nome: string; trechos: string[]}[] = [
  {id: 'olaclick', nome: 'OlaClick', trechos: ['.ola.click', 'olaclick']},
  {id: 'anotaai', nome: 'Anota AI', trechos: ['anota.ai', 'anotaai']},
  {id: 'meucardapio', nome: 'MeuCardápio', trechos: ['meucardapio.ai', 'promokit.com.br']},
  {id: 'cardapioai', nome: 'Cardapio.ai', trechos: ['cardapio.ai']},
  {id: 'goomer', nome: 'Goomer', trechos: ['goomer']},
  {id: 'cardapioweb', nome: 'Cardápio Web', trechos: ['cardapioweb']},
  {id: 'uairango', nome: 'UaiRango', trechos: ['uairango']},
  {id: 'aiqfome', nome: 'aiqfome', trechos: ['aiqfome']},
  {id: 'instadelivery', nome: 'InstaDelivery', trechos: ['instadelivery']},
  {id: 'menudino', nome: 'MenuDino', trechos: ['menudino']},
  {id: 'deliverydireto', nome: 'Delivery Direto', trechos: ['deliverydireto']},
  {id: 'takeat', nome: 'Takeat', trechos: ['takeat']},
  {id: 'pedirdelivery', nome: 'Pedir Delivery', trechos: ['pedir.delivery']},
  {id: 'livemenu', nome: 'Live Menu', trechos: ['livemenu.app']},
  {id: 'yooga', nome: 'Yooga', trechos: ['yooga']},
  {id: 'whatsmenu', nome: 'WhatsMenu', trechos: ['whatsmenu']},
  {id: 'brendi', nome: 'Brendi', trechos: ['brendi']},
  {id: 'ifood', nome: 'iFood', trechos: ['ifood']}
];

export const NOMES_DAS_PLATAFORMAS = ['iFood', 'Anota AI', 'Goomer', 'aiqfome', 'Cardápio Web', 'OlaClick', 'e outras'];

/** "Tenho outro sistema": as plataformas que o link reconhece além do iFood (e do próprio MeuCardápio). */
export const OUTRAS_PLATAFORMAS = PLATAFORMAS.filter(p => p.id !== 'ifood' && p.id !== 'meucardapio').map(p => p.nome);

export function detectePlataforma(link: string): PlataformaDoLink | null {
  const texto = String(link || '').toLowerCase().trim();
  if (!texto) return null;
  const achada = PLATAFORMAS.find(p => p.trechos.some(t => texto.includes(t)));
  return achada ? {id: achada.id, nome: achada.nome} : null;
}

/**
 * Lista os produtos novos da loja na outra plataforma, com os mesmos parâmetros do assistente de configuração, mas
 * sem salvar dados da empresa: o importador sobrescreveria o WhatsApp que a loja usa com a Mia.
 */
export function busqueProdutosDoLink(importador: ImportadorIfoodService, plataforma: string, link: string): Promise<any> {
  const url = link.trim();
  switch (plataforma) {
    case 'ifood': return importador.listeNovosProdutosIfood(url, -1);
    case 'anotaai': return importador.listeNovosProdutosAnotaAi(importador.extraiaSlugLojaAnota(url) || url, -1);
    case 'cardapioweb': return importador.listeNovosProdutosCardapioWeb(url, -1);
    case 'cardapioai': return importador.listeNovosProdutosCardapioAi(url, -1);
    case 'olaclick': return importador.listeNovosProdutosOlaclick(url, -1);
    case 'goomer': return importador.listeNovosProdutosGoomer(url, -1);
    case 'uairango': return importador.listeNovosProdutosUaiRango(url, -1);
    case 'aiqfome': return importador.listeNovosProdutosAiqFome(url, -1);
    case 'instadelivery': return importador.listeNovosProdutosInstaDelivery(url, -1);
    case 'menudino': return importador.listeNovosProdutosMenuDino(url, -1);
    case 'deliverydireto': return importador.listeNovosProdutosDeliveryDireto(url, -1);
    case 'takeat': return importador.listeNovosProdutosTakeat(url, -1);
    case 'yooga': return importador.listeNovosProdutosYooga(url, -1);
    case 'pedirdelivery': return importador.listeNovosProdutosPedirDelivery(url, -1);
    case 'livemenu': return importador.listeNovosProdutosLiveMenu(url, -1);
    case 'whatsmenu': return importador.listeNovosProdutosWhatsMenu(url, -1);
    case 'brendi': return importador.listeNovosProdutosBrendi(url, -1);
    case 'meucardapio': return importador.listeNovosProdutosMeuCardapio(url, -1);
    default: return Promise.reject('Não reconhecemos esse link.');
  }
}

export function extraiaProdutos(resposta: any): any[] {
  if (!resposta) return [];
  if (Array.isArray(resposta)) return resposta;
  return resposta.novosProdutos || resposta.novos || resposta.produtos || [];
}

/** Mesmo preenchimento do assistente: o que faltar no produto da plataforma vira vazio, nunca undefined. */
export function montePayload(produto: any): any {
  const payload = {...produto};
  payload.descricao = payload.descricao || '';
  if (typeof payload.categoria === 'string') payload.categoria = {nome: payload.categoria};
  if (!payload.categoria && payload.nomeCategoria) payload.categoria = {nome: payload.nomeCategoria};
  payload.categoriasAcima = Array.isArray(payload.categoriasAcima) ? payload.categoriasAcima : [];
  payload.camposAdicionais = Array.isArray(payload.camposAdicionais) ? payload.camposAdicionais : (payload.adicionais || []);
  payload.disponibilidades = Array.isArray(payload.disponibilidades) ? payload.disponibilidades : [];
  if (!payload.urlImagemExterna && (payload.imagem || payload.linkImagem)) payload.urlImagemExterna = payload.imagem || payload.linkImagem;
  return payload;
}
